/**
 * People Page
 * 
 * Interface for managing team members in the Prelude application.
 * Features include:
 * - Team member directory with search and filtering
 * - Member profile management
 * - Role and position assignments
 * - Service participation history
 * - Contact information management
 * 
 * Provides comprehensive team member management
 * with proper access control and privacy considerations.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Input,
  useTheme,
  useMediaQuery,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Link,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as LeaderIcon,
  MusicNote as MusicianIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useCustomFetch from '../hooks/useCustomFetch';
import PageHeader from '../components/common/PageHeader';
import { getGravatarUrl, checkGravatarExists, uploadAvatar } from '../utils/avatar';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

const People = () => {
  const { t } = useTranslation();
  const { canEdit, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('firstName');
  const [order, setOrder] = useState('asc');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [instrumentsOpen, setInstrumentsOpen] = useState(false);
  
  // Available instrument options for the multi-select dropdown
  const instrumentOptions = [
    { value: 'vocals', label: 'Vocals' },
    { value: 'drums', label: 'Drums' },
    { value: 'keys', label: 'Keys' },
    { value: 'piano', label: 'Piano' },
    { value: 'electric_guitar', label: 'Electric Guitar' },
    { value: 'acoustic_guitar', label: 'Acoustic Guitar' },
    { value: 'bass', label: 'Bass' },
    { value: 'violin', label: 'Violin' },
    { value: 'music_director', label: 'Music Director' },
    { value: 'sound', label: 'Soundman' },
    { value: 'projection', label: 'Projection' },
    { value: 'other', label: 'Other' }
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { get, post, put, patch, delete: deleteRequest } = useCustomFetch();

  const canManageUsers = () => {
    const userRole = user?.role;
    return userRole === 'admin' || userRole === 'leader';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return format(date, 'PP');
    } catch (err) {
      console.error('Error formatting date:', err);
      return '-';
    }
  };

  const formatPhone = (phone) => {
    return phone || '-';
  };

  const getRoleIcon = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return { icon: <AdminIcon fontSize="small" />, color: '#7B1FA2', tooltip: 'Admin' };
      case 'leader':
        return { icon: <LeaderIcon fontSize="small" />, color: '#1976D2', tooltip: 'Leader' };
      case 'member':
      case 'musician':
      case 'guest':
        return { icon: <PersonIcon fontSize="small" />, color: '#757575', tooltip: 'Member' };
      default:
        return { icon: <PersonIcon fontSize="small" />, color: '#757575', tooltip: 'Member' };
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        sort: orderBy,
        direction: order,
        search: searchTerm || undefined
      };
      
      console.log('Fetching users with params:', params);
      const response = await get('users', { params });
      console.log('Users response:', response);
      
      if (!response) {
        throw new Error('No response received');
      }

      // Handle different response structures
      let userData = Array.isArray(response) ? response : response.data || [];

      const usersWithPhotos = userData.map((user) => ({
        ...user,
        profilePhoto: user.profilePhoto || getGravatarUrl(user.email, !!user.profilePhoto),
        fullName: `${user.firstName} ${user.lastName}`,
        formattedPhone: formatPhone(user.phone),
        formattedDateOfBirth: formatDate(user.dateOfBirth)
      }));

      console.log('Processed users:', usersWithPhotos);
      setUsers(usersWithPhotos);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(t('errors.failedToLoadUsers', 'Failed to load users. Please try again later.'));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [orderBy, order, searchTerm, get, t]);

  // Single effect to handle all data fetching
  useEffect(() => {
    const controller = new AbortController();

    // Debounce search term changes
    const delayDebounceFn = setTimeout(() => {
      fetchUsers();
    }, searchTerm ? 500 : 0); // No delay for initial load or sort changes

    return () => {
      clearTimeout(delayDebounceFn);
      controller.abort();
    };
  }, [searchTerm, orderBy, order, fetchUsers]);

  const handleDeleteUser = async (userId) => {
    try {
      await deleteRequest(`users/${userId}`);
      fetchUsers(); // Refresh the list
      setError(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again later.');
    }
  };

  // Helper function to ensure role is valid and sanitize data
  const ensureValidRole = (userData) => {
    const updatedData = { ...userData };
    
    // Remove any unnecessary fields that could cause issues
    const fieldsToRemove = ['fullName', 'formattedPhone', 'formattedDateOfBirth', '__v'];
    fieldsToRemove.forEach(field => {
      if (updatedData[field] !== undefined) {
        delete updatedData[field];
      }
    });
    
    // Remove password if it exists (should never be sent in updates)
    if (updatedData.password) {
      delete updatedData.password;
    }
    
    return updatedData;
  };

  const handleUpdateUser = async (userData) => {
    try {
      setError(null);
      
      // Log the API endpoint and data for debugging
      console.log('Updating user with ID:', userData._id);
      
      // Ensure role is valid and remove unnecessary fields
      const sanitizedUserData = ensureValidRole(userData);
      
      // Make sure we're not sending the _id in the body as well (some APIs don't like this)
      const { _id, ...userDataWithoutId } = sanitizedUserData;
      
      // Check if we're using the correct API endpoint format
      // The correct format should be: /users/:id (without the /api/v1 prefix)
      // The axios utility will add the baseURL with /api/v1
      const endpoint = `users/${_id}`;
      console.log('Update endpoint:', endpoint);
      
      // Use PATCH instead of PUT since the server expects a PATCH request for user updates
      // Looking at server/routes/userRoutes.js, the endpoint is defined as router.patch('/:id')
      console.log('Sending PATCH request to:', endpoint);
      console.log('Sending data:', userDataWithoutId);
      const response = await patch(endpoint, userDataWithoutId);
      const updatedUser = response.data?.data || response.data || response;
      
      // Update the UI
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user._id === updatedUser._id ? {
            ...updatedUser,
            profilePhoto: updatedUser.profilePhoto || getGravatarUrl(updatedUser.email, !!updatedUser.profilePhoto),
            fullName: `${updatedUser.firstName} ${updatedUser.lastName}`,
            formattedPhone: formatPhone(updatedUser.phone),
            formattedDateOfBirth: formatDate(updatedUser.dateOfBirth)
          } : user
        )
      );
      setIsEditDialogOpen(false);
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user. Please try again later.');
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      setError(null);
      // Ensure role is valid and remove unnecessary fields
      const sanitizedUserData = ensureValidRole(userData);
      console.log('Creating user with data:', sanitizedUserData);
      const response = await post('users', sanitizedUserData);
      const newUser = response.data || response;
      setUsers(prevUsers => [...prevUsers, {
        ...newUser,
        profilePhoto: newUser.profilePhoto || getGravatarUrl(newUser.email, !!newUser.profilePhoto),
        fullName: `${newUser.firstName} ${newUser.lastName}`,
        formattedPhone: formatPhone(newUser.phone),
        formattedDateOfBirth: formatDate(newUser.dateOfBirth)
      }]);
      setIsEditDialogOpen(false);
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Failed to create user. Please try again later.');
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrder('asc');
    }
  };

  const handleEdit = async (user) => {
    if (!canEdit()) {
      alert('You do not have permission to edit users');
      return;
    }
    try {
      // Only use Gravatar if the user doesn't have a profile photo
      if (!user.profilePhoto) {
        user.profilePhoto = getGravatarUrl(user.email, false);
      }
      setSelectedUser(user);
      setIsEditDialogOpen(true);
    } catch (err) {
      console.error('Error checking Gravatar:', err);
      setSelectedUser(user);
      setIsEditDialogOpen(true);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      setUploadError(null);
      const url = await uploadAvatar(file);
      setSelectedUser(prev => ({
        ...prev,
        profilePhoto: url
      }));
    } catch (err) {
      console.error('Error uploading file:', err);
      setUploadError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Ensure we have the correct data structure for the API
      const userData = {
        ...values,
        password: values.password || 'pass1234' // Set default password if not provided
      };

      // Log the data being sent for debugging
      console.log('Submitting user data:', userData);
      
      if (selectedUser && selectedUser._id) {
        // Make sure the _id is included in the update data
        await handleUpdateUser({
          ...userData,
          _id: selectedUser._id
        });
      } else {
        await handleCreateUser(userData);
      }
      setIsEditDialogOpen(false);
      fetchUsers();
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.message || 'Failed to save changes. Please try again.');
    }
    setSubmitting(false);
  };

  const handleDelete = async (userId) => {
    if (!canEdit()) {
      alert('You do not have permission to delete users');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await handleDeleteUser(userId);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'dateOfBirth') {
      const d = new Date(value);
      setSelectedUser(prev => ({
        ...prev,
        dateOfBirth: !isNaN(d.getTime()) ? d.toISOString() : null
      }));
    } else {
      setSelectedUser(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleEmailBlur = async (e) => {
    const email = e.target.value;
    if (email && email !== selectedUser.originalEmail) {
      const hasGravatar = await checkGravatarExists(email);
      setSelectedUser(prev => ({
        ...prev,
        originalEmail: email,
        profilePhoto: hasGravatar ? getGravatarUrl(email, false) : '/path/to/default/image.png'
      }));
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortUsers = (users) => {
    return [...users].sort((a, b) => {
      let aValue = '';
      let bValue = '';

      switch (orderBy) {
        case 'name':
          aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'role':
          aValue = a.role.toLowerCase();
          bValue = b.role.toLowerCase();
          break;
        default:
          return 0;
      }

      if (order === 'desc') {
        return bValue.localeCompare(aValue);
      }
      return aValue.localeCompare(bValue);
    });
  };

  const SortableTableCell = ({ id, label, align = 'left' }) => {
    const isActive = orderBy === id;
    
    return (
      <TableCell
        align={align}
        sortDirection={isActive ? order : false}
        sx={{ 
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: 'action.hover',
          }
        }}
        onClick={() => handleRequestSort(id)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {label}
          <Box sx={{ display: 'flex', flexDirection: 'column', opacity: isActive ? 1 : 0.3 }}>
            <KeyboardArrowUpIcon 
              sx={{ 
                fontSize: 18,
                opacity: order === 'asc' && isActive ? 1 : 0.3,
                mb: -1
              }} 
            />
            <KeyboardArrowDownIcon 
              sx={{ 
                fontSize: 18,
                opacity: order === 'desc' && isActive ? 1 : 0.3,
                mt: -1
              }} 
            />
          </Box>
        </Box>
      </TableCell>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ 
        width: '100%', 
        p: { xs: 2, sm: 3 },
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PageHeader
            title={t('people.title')}
            subtitle={t('people.subtitle')}
            onAddClick={() => {
              console.log('[People] Add button clicked - initializing new user');
              setSelectedUser({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                role: 'member',
                dateOfBirth: null,
                instruments: [],
              });
              setIsEditDialogOpen(true);
            }}
            showAddButton={canManageUsers()}
          />
          
          <Paper sx={{ width: '100%', overflow: 'hidden', bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1A1A1A' : 'white' }}>
            <Box sx={{ p: 2 }}>
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                sx={{ 
                  mb: 2,
                  width: '100%',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between'
                }}
              >
                <TextField
                  placeholder={t('people.searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: { xs: '100%', sm: '300px' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Box>

            {/* Desktop Table View */}
            <Box sx={{ display: { xs: 'none', md: 'block' }, height: '100%' }}>
              <TableContainer sx={{ height: '100%' }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <SortableTableCell id="name" label={t('people.name')} />
                      <SortableTableCell id="email" label={t('people.email')} />
                      <SortableTableCell id="role" label={t('people.role')} />
                      {canManageUsers() && (
                        <TableCell align="right">{t('people.actions')}</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortUsers(users).map((user) => (
                      <TableRow
                        key={user._id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar 
                              alt={`${user.firstName} ${user.lastName}`}
                              src={user.profilePhoto}
                              sx={{ 
                                width: 32, 
                                height: 32,
                                bgcolor: (theme) => !user.profilePhoto ? theme.palette.primary.main : 'transparent'
                              }}
                            >
                              {!user.profilePhoto ? `${user.firstName[0]}${user.lastName[0]}` : null}
                            </Avatar>
                            <Typography>{user.firstName} {user.lastName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Link
                            href={`mailto:${user.email}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            <EmailIcon fontSize="small" />
                            {user.email}
                          </Link>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {t(`roles.${user.role}`)}
                        </TableCell>
                        {canManageUsers() && (
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {user.phone && (
                              <Tooltip title={`SMS: ${user.phone}`}>
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(`sms:${user.phone}`)}
                                  color="success"
                                >
                                  <SmsIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(user)}
                              sx={{ color: '#a259ff' }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(user._id)}
                              sx={{ color: '#ff5252' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Mobile Card View */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <Stack spacing={2}>
                {sortUsers(users).map((user) => (
                  <Paper
                    key={user._id}
                    elevation={1}
                    sx={{
                      p: 2,
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1A1A1A' : 'white',
                    }}
                  >
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar 
                            alt={`${user.firstName} ${user.lastName}`}
                            src={user.profilePhoto}
                            sx={{ 
                              width: 32, 
                              height: 32,
                              bgcolor: (theme) => !user.profilePhoto ? theme.palette.primary.main : 'transparent'
                            }}
                          >
                            {!user.profilePhoto ? `${user.firstName[0]}${user.lastName[0]}` : null}
                          </Avatar>
                          <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold' }}>
                            {user.firstName} {user.lastName}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Link
                        href={`mailto:${user.email}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        <EmailIcon fontSize="small" />
                        {user.email}
                      </Link>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip
                          label={t(`roles.${user.role}`)}
                          variant="outlined"
                          size="small"
                        />
                        <Box>
                          {user.phone && (
                            <Tooltip title={`SMS: ${user.phone}`}>
                              <IconButton
                                size="small"
                                onClick={() => window.open(`sms:${user.phone}`)}
                                color="success"
                              >
                                <SmsIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canManageUsers() && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(user)}
                                sx={{ color: '#a259ff' }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(user._id)}
                                sx={{ color: '#ff5252' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Paper>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Box sx={{ textAlign: 'center', my: 4 }}>
              <Typography variant="h6" color="text.secondary">
                {t('people.noPeopleFound')}
              </Typography>
              <Typography color="text.secondary">
                {searchTerm 
                  ? t('people.tryAdjustingSearchTerms')
                  : t('people.noUsersInSystemYet')}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
        </Box>
      </Box>

      {/* Edit User Dialog */}
      <Dialog 
        open={isEditDialogOpen} 
        onClose={() => setIsEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1A1A1A' : 'white'
          }
        }}
      >
        <DialogTitle>
          {selectedUser?._id ? t('people.editUser') : t('people.addNew')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Profile Photo Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={selectedUser?.profilePhoto || (selectedUser?.email && getGravatarUrl(selectedUser.email, !!selectedUser?.profilePhoto))}
                alt={selectedUser?.firstName}
                sx={{ width: 80, height: 80 }}
              >
                <PersonIcon sx={{ width: 40, height: 40 }} />
              </Avatar>
              <Box>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="profile-photo-upload"
                  onChange={handleFileUpload}
                />
                <label htmlFor="profile-photo-upload">
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    disabled={uploading}
                  >
                    {uploading ? t('people.form.uploading') : t('people.form.uploadPhoto')}
                  </Button>
                </label>
                {uploadError && (
                  <Typography color="error" variant="caption" display="block">
                    {uploadError}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
              <TextField
                autoFocus
                name="firstName"
                label={t('people.form.firstName')}
                type="text"
                fullWidth
                variant="outlined"
                value={selectedUser?.firstName || ''}
                onChange={handleInputChange}
                required
              />
              <TextField
                name="lastName"
                label={t('people.form.lastName')}
                type="text"
                fullWidth
                variant="outlined"
                value={selectedUser?.lastName || ''}
                onChange={handleInputChange}
                required
              />
            </Box>
            
            <TextField
              name="email"
              label={t('people.form.email')}
              type="email"
              fullWidth
              variant="outlined"
              value={selectedUser?.email || ''}
              onChange={handleInputChange}
              onBlur={handleEmailBlur}
              required
            />

            <TextField
              name="phone"
              label={t('people.form.phone')}
              type="tel"
              fullWidth
              variant="outlined"
              value={selectedUser?.phone || ''}
              onChange={handleInputChange}
            />

            <FormControl fullWidth variant="outlined">
              <InputLabel id="role-select-label">{t('people.form.role')}</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                name="role"
                value={selectedUser?.role || 'member'}
                onChange={handleInputChange}
                label={t('people.form.role')}
              >
                {user?.role === 'admin' && (
                  <MenuItem value="admin">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        sx={{
                          backgroundColor: `${getRoleIcon('admin').color}15`,
                          color: getRoleIcon('admin').color,
                          '&:hover': {
                            backgroundColor: `${getRoleIcon('admin').color}25`,
                          },
                          padding: '4px',
                        }}
                      >
                        {getRoleIcon('admin').icon}
                      </IconButton>
                      <Typography>{t('people.roles.admin')}</Typography>
                    </Box>
                  </MenuItem>
                )}
                <MenuItem value="leader">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      sx={{
                        backgroundColor: `${getRoleIcon('leader').color}15`,
                        color: getRoleIcon('leader').color,
                        '&:hover': {
                          backgroundColor: `${getRoleIcon('leader').color}25`,
                        },
                        padding: '4px',
                      }}
                    >
                      {getRoleIcon('leader').icon}
                    </IconButton>
                    <Typography>{t('people.roles.leader')}</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="member">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      sx={{
                        backgroundColor: `${getRoleIcon('member').color}15`,
                        color: getRoleIcon('member').color,
                        '&:hover': {
                          backgroundColor: `${getRoleIcon('member').color}25`,
                        },
                        padding: '4px',
                      }}
                    >
                      {getRoleIcon('member').icon}
                    </IconButton>
                    <Typography>{t('people.roles.member', 'Member')}</Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <DatePicker
              label={t('people.form.dateOfBirth')}
              value={(() => {
                if (!selectedUser?.dateOfBirth) return null;
                const d = new Date(selectedUser.dateOfBirth);
                return isNaN(d.getTime()) ? null : d;
              })()}
              onChange={(newValue) => {
                setSelectedUser(prev => ({
                  ...prev,
                  dateOfBirth: newValue && !isNaN(newValue.getTime()) ? newValue.toISOString() : null
                }));
              }}
              renderInput={(params) => <TextField {...params} fullWidth variant="outlined" />}
            />
            
            <FormControl fullWidth variant="outlined">
              <InputLabel id="instruments-select-label">{t('people.form.instruments')}</InputLabel>
              <Select
                labelId="instruments-select-label"
                id="instruments-select"
                multiple
                open={instrumentsOpen}
                onOpen={() => setInstrumentsOpen(true)}
                onClose={() => setInstrumentsOpen(false)}
                value={selectedUser?.instruments || []}
                onChange={(e) => {
                  setSelectedUser(prev => ({
                    ...prev,
                    instruments: e.target.value
                  }));
                  setInstrumentsOpen(false);
                }}
                input={<OutlinedInput label={t('people.form.instruments')} />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const option = instrumentOptions.find(opt => opt.value === value);
                      return (
                        <Chip 
                          key={value} 
                          label={option ? t(`positions.${value}`, option.label) : value}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {instrumentOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={(selectedUser?.instruments || []).indexOf(option.value) > -1} />
                    <ListItemText primary={t(`positions.${option.value}`, option.label)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsEditDialogOpen(false)} color="inherit">
            {t('people.form.cancel')}
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            onClick={() => {
              const setSubmitting = (isSubmitting) => {};
              handleSubmit(selectedUser, { setSubmitting });
            }}
          >
            {t('people.form.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default People;
